/**
 * Decimal money to ISO 4217 minor units, for APIs that count in integers.
 * Shared pure module (tracking + backend, see tsconfig.build.json files):
 * the OpenAI adapter AND the browser pixel convert with the SAME table, so
 * the two dedup halves can never disagree about an amount.
 *
 * OpenAI's Conversions API wants `data.amount` as an INTEGER in the
 * currency's minor unit: 2599 means $25.99, but 5000 JPY means 5000 yen,
 * because the yen has no minor unit. A naive `value * 100` is wrong twice:
 * off by 100x for zero- and three-decimal currencies, and off by one cent
 * for float artifacts (19.99 * 100 === 1998.9999...).
 */

/** ISO 4217 currencies with no minor unit (exponent 0). */
const ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  "BIF", "CLP", "DJF", "GNF", "ISK", "JPY", "KMF", "KRW",
  "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF",
]);

/** ISO 4217 currencies with a three-decimal minor unit (exponent 3). */
const THREE_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  "BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND",
]);

const DEFAULT_EXPONENT = 2;

function exponentFor(currency: string): number {
  const upper = currency.toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(upper)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(upper)) return 3;
  return DEFAULT_EXPONENT;
}

/**
 * 49.95 EUR -> 4995; 5000 JPY -> 5000; 1.234 KWD -> 1234.
 * Math.round, not truncation: 19.99 * 100 is 1998.999... in floats.
 */
export function toMinorUnits(value: number, currency: string): number {
  return Math.round(value * 10 ** exponentFor(currency));
}
